import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Btn, EmptyState } from "@/components/primitives";
import { PdfModal } from "@/components/PdfModal";
import { fmtDate } from "@/lib/format";
import { FileSpreadsheet, FileText, Info } from "lucide-react";

const STATUS_PILL = {
  draft: "bg-curtron-muted/15 text-curtron-muted",
  submitted: "bg-curtron-ochre/15 text-curtron-ochre",
  paid: "bg-curtron-green/15 text-curtron-green",
};

export default function DriverLog() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState(null);
  const [viewPath, setViewPath] = useState(null);
  const [viewTitle, setViewTitle] = useState("");

  useEffect(() => { api.get("/driver-log").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  if (items === null) return <Loading />;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Driver Logs" subtitle={isAdmin ? "Pay-period trip & expense logs." : "Your pay-period logs."} />

      <div className="flex gap-2.5 rounded-md border border-curtron-info/30 bg-curtron-info/8 px-4 py-3 mb-4" data-testid="driverlog-note">
        <Info className="w-4 h-4 text-curtron-info shrink-0 mt-0.5" />
        <p className="text-sm text-curtron-info">Driver logs are filled out in the Curtron Hub mobile app. Here you can review submitted logs and download the exact-replica PDF.</p>
      </div>

      {items.length === 0 ? (
        <Card><EmptyState icon={FileSpreadsheet} title="No driver logs" subtitle="Logs created in the mobile app will appear here." /></Card>
      ) : (
        <div className="space-y-3">
          {items.map((l) => (
            <Card key={l.id} className="p-4 flex items-center gap-3" data-testid={`driverlog-${l.id}`}>
              <span className="w-10 h-10 rounded-md bg-curtron-green/10 text-curtron-green flex items-center justify-center shrink-0"><FileSpreadsheet className="w-5 h-5" /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-curtron-ink text-sm truncate">{l.period_label || `${fmtDate(l.period_start, false)} – ${fmtDate(l.period_end, false)}`}</span>
                  <span className={`text-[10px] uppercase font-semibold tracking-wide rounded-full px-2 py-0.5 shrink-0 ${STATUS_PILL[l.status] || "bg-curtron-muted/15 text-curtron-muted"}`}>{l.status}</span>
                </div>
                <div className="text-xs text-curtron-muted truncate mt-0.5">
                  {isAdmin && l.driver_name ? `${l.driver_name} · ` : ""}{(l.trip_entries?.length || 0)} trips · {(l.card_entries?.length || 0)} card entries
                  {l.date_paid ? ` · Paid ${fmtDate(l.date_paid, false)}${l.check_number ? ` (#${l.check_number})` : ""}` : ""}
                </div>
              </div>
              <Btn variant="secondary" className="h-8" onClick={() => { setViewTitle(l.period_label || "Driver Log"); setViewPath(`/driver-log/${l.id}/pdf`); }} data-testid={`driverlog-pdf-${l.id}`}>
                <FileText className="w-4 h-4" /> PDF
              </Btn>
            </Card>
          ))}
        </div>
      )}

      <PdfModal path={viewPath} title={viewTitle} onClose={() => setViewPath(null)} />
    </div>
  );
}
